import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProduct, createProduct, updateProduct } from "@/services/firebase/product";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";
import { AdminPage, AdminPageHeader, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const productSchema = z.object({
  name: z.string().min(1, "商品名稱為必填"),
  price: z.number().min(0, "價格必須大於等於 0"),
  status: z.enum(["active", "inactive"]),
  stock_quantity: z.number().min(0).optional().nullable(),
  main_image: z.string().min(1, "商品主圖為必填"),
  additional_images: z.array(z.string()).optional(),
  description: z.string().min(1, "商品描述為必填"),
  category: z.enum(["merchandise", "clothing", "collectibles", "digital", "other"]).optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

export default function ProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入商品數據
  const { data: product, isLoading } = useQuery({
    queryKey: ["product", actualId],
    queryFn: () => getProduct(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      price: 0,
      status: "active",
      stock_quantity: undefined,
      main_image: "",
      additional_images: [],
      description: "",
      category: undefined,
    },
  });

  const mainImage = watch("main_image");
  const status = watch("status");
  const category = watch("category");

  // 初始化表單數據
  useEffect(() => {
    if (product && !isNew) {
      setValue("name", product.name || "");
      setValue("price", product.price || 0);
      setValue("status", product.status || "active");
      setValue("stock_quantity", product.stock_quantity);
      setValue("main_image", product.main_image || "");
      setValue("additional_images", product.additional_images || []);
      setValue("description", product.description || "");
      setValue("category", product.category);
    }
  }, [product, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      if (isNew) {
        return await createProduct({
          name: data.name,
          price: data.price,
          status: data.status,
          stock_quantity: data.stock_quantity || undefined,
          main_image: data.main_image,
          additional_images: data.additional_images,
          description: data.description,
          category: data.category,
        });
      } else {
        if (!actualId) {
          throw new Error("缺少商品 ID");
        }
        await updateProduct(actualId, {
          name: data.name,
          price: data.price,
          status: data.status,
          stock_quantity: data.stock_quantity || undefined,
          main_image: data.main_image,
          additional_images: data.additional_images,
          description: data.description,
          category: data.category,
        });
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", savedId] });
      setSaveError(null);
      navigate("/products");
    },
    onError: (error) => {
      console.error("儲存商品失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: ProductFormData) => {
    mutation.mutate(data);
  };

  const handleImageUpload = (url: string) => {
    setValue("main_image", url, { shouldDirty: true, shouldValidate: true });
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增商品" : "編輯商品"}
        actions={
          <Button variant="outline" onClick={() => navigate("/products")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 錯誤訊息 */}
        {saveError && (
          <AdminErrorAlert
            message={`儲存失敗：${saveError}`}
            onDismiss={() => setSaveError(null)}
          />
        )}

        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                商品名稱 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="例如：限量版T恤"
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">
                  價格 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register("price", { valueAsNumber: true })}
                  placeholder="0"
                  className={errors.price ? "border-destructive" : ""}
                />
                {errors.price && (
                  <p className="text-sm text-destructive">{errors.price.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="stock_quantity">庫存數量</Label>
                <Input
                  id="stock_quantity"
                  type="number"
                  min="0"
                  {...register("stock_quantity", { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  狀態 <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={status}
                  onValueChange={(value) =>
                    setValue("status", value as "active" | "inactive")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇狀態" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">上架中</SelectItem>
                    <SelectItem value="inactive">已下架</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>商品分類</Label>
                <Select
                  value={category || ""}
                  onValueChange={(value) =>
                    setValue("category", value as ProductFormData["category"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="請選擇" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merchandise">周邊商品</SelectItem>
                    <SelectItem value="clothing">服飾配件</SelectItem>
                    <SelectItem value="collectibles">收藏品</SelectItem>
                    <SelectItem value="digital">數位商品</SelectItem>
                    <SelectItem value="other">其他</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 圖片 */}
        <Card>
          <CardHeader>
            <CardTitle>商品圖片</CardTitle>
          </CardHeader>
          <CardContent>
            <ImageUpload
              value={mainImage}
              onChange={handleImageUpload}
              storagePath="product_images"
              label="商品主圖"
              required={true}
              error={errors.main_image?.message}
            />
          </CardContent>
        </Card>

        {/* 描述 */}
        <Card>
          <CardHeader>
            <CardTitle>商品描述</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="description">
                描述 <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                {...register("description")}
                rows={6}
                placeholder="輸入商品的詳細描述..."
                className={errors.description ? "border-destructive" : ""}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/products")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
